import React, { useState, useEffect } from "react";
import {
  showError,
  showSuccess,
} from "../../../../../../../../utils/showMessage.js";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Images from "../../../../../../../../utils/images.js";
import CrossButton from "../../../../../../../common/ButtonSpinner/CrossButton.js";
import GeneralButton from "../../../../../../../common/SaveButton/GeneralButton.js";
import CustomModal from "../../../../../../../common/CustomModal/CustomModal.js";
import "react-toastify/dist/ReactToastify.css";
import { getColumns } from "../../../../../../../../api/database_gui.js";

const RemoveColumnPopup = ({
  visible,
  onClose,
  onSave,
  tableName,
  resourceId,
  systemUserId,
  databaseName,
  databaseInfo,
  availableColumns,
  selectedColumn, // Add this prop for single column removal
  loading,
  setLoading,
}) => {
  const [selectedColumns, setSelectedColumns] = useState(
    selectedColumn ? [selectedColumn] : []
  );
  const [columns, setColumns] = useState(availableColumns || []);
  const [columnsLoading, setColumnsLoading] = useState(false);

  // Fetch latest columns in "real time" whenever popup opens
  useEffect(() => {
    const fetchColumns = async () => {
      if (!visible || !resourceId || !systemUserId || !databaseName || !tableName) return;

      try {
        setColumnsLoading(true);
        const { data, error } = await getColumns(
          resourceId,
          systemUserId,
          databaseName,
          tableName
        );

        if (error) {
          showError("Failed to fetch latest columns for this table.");
          return;
        }

        if (data && data.columns) {
          // Backend returns columns as [[col1], [col2], ...]
          const columnNames = data.columns.map((col) =>
            Array.isArray(col) ? col[0] : col
          );
          setColumns(columnNames);
          // Reset selection for multi-select case
          if (!selectedColumn) {
            setSelectedColumns([]);
          }
        }
      } catch (e) {
        showError("Failed to fetch latest columns for this table.");
      } finally {
        setColumnsLoading(false);
      }
    };

    fetchColumns();
  }, [visible, resourceId, systemUserId, databaseName, tableName, selectedColumn]);

  const handleToggleColumn = (column) => {
    setSelectedColumns((prev) =>
      prev.includes(column)
        ? prev.filter((c) => c !== column)
        : [...prev, column]
    );
  };

  const handleConfirm = async () => {
    try {
      if (selectedColumn) {
        // Remove single column
        onSave(resourceId, systemUserId, databaseName, tableName, [selectedColumn]);
      } else {
        // Remove selected columns (table-level operation)
        if (!selectedColumns || selectedColumns.length === 0) {
          showError("Please select at least one column to remove.");
          return;
        }
        onSave(resourceId, systemUserId, databaseName, tableName, selectedColumns);
      }
    } catch (e) {
      showError("Error removing columns. Please try again.");
    }
  };

  return (
    <CustomModal 
      open={visible} 
      handleClose={onClose}
      width="35%"
    >
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <div className="d-flex align-items-center mb-1">
              {databaseInfo === "MySQL" && (
                <img src={Images.mysql} alt="MySQL" width="24" height="24" className="me-2" />
              )}
              {databaseInfo === "PostgreSQL" && (
                <img src={Images.pssql} alt="PostgreSQL" width="24" height="24" className="me-2" />
              )}
              {databaseInfo === "MSSQL" && (
                <img src={Images.mssql} alt="MSSQL" width="24" height="24" className="me-2" />
              )}
              <h3 className="border-0 customize-column-name mb-0">
                {selectedColumn ? `Remove Column: ${selectedColumn}` : "Remove Columns"}
              </h3>
            </div>
            <p className="text-muted mb-0 fs-14px">
              {selectedColumn 
                ? `Remove the column "${selectedColumn}" from the table.`
                : "Remove columns from the table."
              }
            </p>
          </div>
          <CrossButton onClick={onClose} />
        </div>

        {!selectedColumn && (
          <div className="mb-4">
            <p className="mb-2">Select columns to remove:</p>
            <div
              className="border rounded p-2"
              style={{ maxHeight: "220px", overflowY: "auto" }}
            >
              {columnsLoading && (
                <p className="text-muted mb-0">Loading columns...</p>
              )}
              {!columnsLoading && (!columns || columns.length === 0) && (
                <p className="text-muted mb-0">
                  No columns available for this table.
                </p>
              )}
              {!columnsLoading &&
                columns &&
                columns.map((column) => (
                  <div className="form-check" key={column}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`remove-col-${column}`}
                      checked={selectedColumns.includes(column)}
                      onChange={() => handleToggleColumn(column)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`remove-col-${column}`}
                    >
                      {column}
                    </label>
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className="mb-4">
          <div className="alert alert-warning py-3">
            <div className="d-flex align-items-start">
              <img
                src={Images.InfoIcon}
                alt="warning"
                width="16"
                height="16"
                className="me-2 mt-1"
              />
              <div>
                <strong>Warning:</strong> Are you sure you want to remove{" "}
                {selectedColumn ? (
                  <>
                    column <code className="bg-light px-2 py-1 rounded">{selectedColumn}</code> from table{" "}
                    <code className="bg-light px-2 py-1 rounded">{tableName}</code>
                  </>
                ) : (
                  <>
                    columns from table <code className="bg-light px-2 py-1 rounded">{tableName}</code>
                  </>
                )}{" "}
                in database <code className="bg-light px-2 py-1 rounded">{databaseName}</code>?
              </div>
            </div>
          </div>
          <small className="text-muted">
            This action cannot be undone and will permanently delete the {selectedColumn ? "column and its" : "selected columns and their"} data.
          </small>
        </div>

        <Row className="pt-4 mb-3 justify-content-end">
          <Col sm="auto">
            <GeneralButton
              onClickEvent={onClose}
              className="me-1"
              value="Cancel"
              color="#505050"
              variant="outlined"
              size="large"
            />
          </Col>
          <Col sm="auto" className="me-sm-2 p-0">
            {loading ? (
              <GeneralButton
                onClickEvent={handleConfirm}
                variant="contained"
                disabled={true}
                className="me-1"
                value={
                  <img src={Images.Loader} width="26px" height="26px" />
                }
                size="large"
              />
            ) : (
              <GeneralButton
                onClickEvent={handleConfirm}
                className="me-1"
                value="Remove"
                variant="contained"
                size="large"
              />
            )}
          </Col>
        </Row>
      </div>
    </CustomModal>
  );
};

export default RemoveColumnPopup; 